import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { useSiteImages, pick } from "@/lib/siteImages";
import {
  Baby, Flower2, Flower, Dog, PawPrint, Cat, House, Heart, HeartHandshake,
  Rainbow, Footprints, Milk, Clover, Star, Sparkles, Check, Loader2, Send, Truck, PenLine,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAYPAL_EMAIL = process.env.REACT_APP_PAYPAL_EMAIL || "";

const DEFAULT_PRICING = {
  catena_rosario: 32,
  catena_perle: 34,
  catena_rolo: 29,
  soggetto_petit: 14,
  soggetto_moments: 16,
  soggetto_bonbon: 14,
  base_bonbon: 39,
  base_rigido: 28,
  base_morbido: 24,
  base_collana: 34,
  lettera: 3,
  lettera_lucida: 3,
  lettera_zirconata: 4,
  soggetto_legami: 8,
  extra_zirconati: 6,
  extra_lettere_zirconate: 6,
  "legami_bambino-zirconato": 8,
  "legami_bambina-zirconata": 8,
  "legami_cuore-zirconato": 8,
  "legami_zampa-zirconata": 8,
  "legami_quadrifoglio-zirconato": 8,
  "legami_stella-zirconata": 8,
  "legami_farfalla-zirconata": 8,
  "legami_bambino-smaltato-blu": 8,
  "legami_bambina-smalto-rosa": 8,
  "legami_cuore-lucido": 8,
  iniziale: 10,
  spedizione: 8,
  spedizione_gratis_sopra: 70,
};

const COLLEZIONI = [
  { id: "petit", nome: "Collezione Petit", desc: "Collana con soggetti e incisioni", image: "https://images.unsplash.com/photo-1787084050199-0f399878a8a2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHw0fHxnb2xkJTIwbWVkYWxsaW9uJTIwcGVuZGFudCUyMG5lY2tsYWNlfGVufDB8fHx8MTc5MTgxNjM0N3ww&ixlib=rb-4.1.0&q=85" },
  { id: "legami", nome: "Collezione Legami", desc: "Bracciale componibile con lettere", image: "https://images.unsplash.com/photo-1786118791301-bede74b9c3b2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY6NjV8MHwxfHNlYXJjaHwzfHxsZXR0ZXIlMjBiZWFkcyUyMGJyYWNlbGV0JTIwd3Jpc3R8ZW58MHx8fHwxNzkxODE2MzUxfDA&ixlib=rb-4.1.0&q=85" },
  { id: "my-letters", nome: "Collezione My Letters", desc: "Iniziali in argento", image: "https://images.unsplash.com/photo-1784751722900-c5f70f8b667e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxpbml0aWFsJTIwbGV0dGVyJTIwcGVuZGFudCUyMG5lY2tsYWNlfGVufDB8fHx8MTc5MTgxNjM0MHww&ixlib=rb-4.1.0&q=85" },
  { id: "bon-bon", nome: "Collezione Bon Bon", desc: "Collana con perle", image: "https://images.unsplash.com/photo-1613315986155-820432757035?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwzfHxlbGVnYW50JTIwcGVhcmwlMjBuZWNrbGFjZSUyMHdvcm58ZW58MHx8fHwxNzkxODE2MzQwfDA&ixlib=rb-4.1.0&q=85" },
  { id: "moments", nome: "Collezione Moments", desc: "Solo pendente", image: "https://images.unsplash.com/photo-1702476320482-0736c4b962f5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHxsb29zZSUyMGpld2VscnklMjBjaGFybXMlMjBjb2xsZWN0aW9uJTIwZmxhdCUyMGxheXxlbnwwfHx8fDE3OTE4MTYzNDB8MA&ixlib=rb-4.1.0&q=85" },
];

const CATENE = [
  { id: "rosario", label: "Catena Rosario", image: "https://images.unsplash.com/photo-1625792508553-5e66a81659fa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHszfHxiZWFkZWQlMjByb3NhcnklMjBjaGFpbiUyMG5lY2tsYWNlJTIwamV3ZWxyeSUyMGdvbGR8ZW58MHx8fHwxNzkxODE3NzcyfDA&ixlib=rb-4.1.0&q=85" },
  { id: "perle", label: "Catena Perle", image: "https://images.unsplash.com/photo-1613315986155-820432757035?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwzfHxlbGVnYW50JTIwcGVhcmwlMjBuZWNrbGFjZSUyMHdvcm58ZW58MHx8fHwxNzkxODE2MzQwfDA&ixlib=rb-4.1.0&q=85" },
  { id: "rolo", label: "Catena Rolò", image: "https://images.unsplash.com/photo-1751552147774-c374ae8e9910?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHszfHxnb2xkJTIwcm9sbyUyMGNoYWluJTIwbmVja2xhY2UlMjBjbG9zZSUyMHVwJTIwamV3ZWxyeXxlbnwwfHx8fDE3OTE4MTc3NzJ8MA&ixlib=rb-4.1.0&q=85" },
];

const GALVANICHE = [
  { id: "argento-bianco", label: "Argento Bianco", swatch: "#D9D9DE" },
  { id: "argento-giallo", label: "Argento Giallo", swatch: "#D4AF37" },
  { id: "argento-rosa", label: "Argento Rosa", swatch: "#E0A387" },
];

const GALVANICA_LETTERE_ROSA = { id: "bianco-lettere-rosa", label: "Argento Bianco · Lettere Rosa", swatch: "linear-gradient(135deg, #D9D9DE 50%, #E2A3B1 50%)" };

const GALVANICHE_LETTERE_ROSA = [
  ...GALVANICHE,
  GALVANICA_LETTERE_ROSA,
];

const PERLE = [
  { id: "bianche", label: "Perle Bianche", swatch: "#F5F2EC" },
  { id: "rosa", label: "Perle Rosa", swatch: "#EFC9D1" },
  { id: "azzurre", label: "Perle Azzurre", swatch: "#BFD6E4" },
];

const TIPI_SOGGETTO = [
  { id: "bimbo", label: "Bimbo", icon: Baby, image: "https://images.unsplash.com/photo-1753558354270-5cd4f5c3a0de?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY6Njd8MHwxfHNlYXJjaHwzfHxib3klMjBjaGFybSUyMHBlbmRhbnQlMjBnb2xkJTIwamV3ZWxyeXxlbnwwfHx8fDE3OTE4MTc3Nzh8MA&ixlib=rb-4.1.0&q=85" },
  { id: "bimba", label: "Bimba", icon: Flower2, image: "https://images.unsplash.com/photo-1728544804232-ab824bdbff25?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwyfHxnaXJsJTIwc2lsaG91ZXR0ZSUyMGNoYXJtJTIwcGVuZGFudCUyMGdvbGQlMjBqZXdlbHJ5fGVufDB8fHx8MTc5MTgxNzc3OHww&ixlib=rb-4.1.0&q=85" },
  { id: "cane", label: "Cane", icon: Dog, image: "https://images.unsplash.com/photo-1669247565558-d0dc5fdf8c73?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY6Nzd8MHwxfHNlYXJjaHwzfHxkb2clMjBwYXclMjBwZW5kYW50JTIwY2hhcm0lMjBnb2xkJTIwamV3ZWxyeXxlbnwwfHx8fDE3OTE4MTc3Nzh8MA&ixlib=rb-4.1.0&q=85" },
  { id: "orsetto", label: "Orsetto", icon: PawPrint, image: "https://images.unsplash.com/photo-1623302312645-65df66384692?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwyfHxjaGlsZCUyMHBlbmRhbnQlMjBnb2xkJTIwamV3ZWxyeXxlbnwwfHx8fDE3OTE4MTc3OTJ8MA&ixlib=rb-4.1.0&q=85" },
  { id: "gatto", label: "Gatto", icon: Cat, image: "https://images.unsplash.com/photo-1676084979040-bfd92303bd64?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxjYXQlMjBwZW5kYW50JTIwY2hhcm0lMjBnb2xkJTIwamV3ZWxyeXxlbnwwfHx8fDE3OTE4MTc3Nzh8MA&ixlib=rb-4.1.0&q=85" },
  { id: "casa", label: "Casa", icon: House, image: "https://images.unsplash.com/photo-1573770403113-8e370c14c4b3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY6NjZ8MHwxfHNlYXJjaHwxfHxob3VzZSUyMHBlbmRhbnQlMjBjaGFybSUyMGdvbGQlMjBqZXdlbHJ5fGVufDB8fHwxNzkxODE3Nzc4fDA&ixlib=rb-4.1.0&q=85" },
  { id: "fiore", label: "Fiore", icon: Flower, image: "https://images.unsplash.com/photo-1708222169717-8251cfdfa608?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY6Nzh8MHwxfHNlYXJjaHwyfHxmbG93ZXIlMjBwZW5kYW50JTIwY2hhcm0lMjBnb2xkJTIwamV3ZWxyeXxlbnwwfHx8fDE3OTE4MTc3Nzh8MA&ixlib=rb-4.1.0&q=85" },
  { id: "cuore", label: "Cuore", icon: Heart, image: "https://images.unsplash.com/photo-1708222169031-590f016d7451?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHszfHxoZWFydCUyMHBlbmRhbnQlMjBnb2xkJTIwbmVja2xhY2UlMjBjbG9zZSUyMHVwfGVufDB8fHx8MTc5MTgxNzc3Mnww&ixlib=rb-4.1.0&q=85" },
  { id: "cuore-amiche", label: "Cuore Amiche", icon: HeartHandshake, image: "https://images.unsplash.com/photo-1623321673989-830eff0fd59f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHxoZWFydCUyMHBlbmRhbnQlMjBnb2xkJTIwbmVja2xhY2UlMjBjbG9zZSUyMHVwfGVufDB8fHx8MTc5MTgxNzc3Mnww&ixlib=rb-4.1.0&q=85" },
  { id: "arcobaleno-bimbo", label: "Arcobaleno Bimbo", icon: Rainbow, image: "https://images.unsplash.com/photo-1728544804234-9c36ab857d43?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwyfHxyYWluYm93JTIwY2hhcm0lMjBwZW5kYW50JTIwZ29sZCUyMGpld2Vscnl8ZW58MHx8fHwxNzkxODE3NzgzfDA&ixlib=rb-4.1.0&q=85" },
  { id: "arcobaleno-bimba", label: "Arcobaleno Bimba", icon: Rainbow, image: "https://images.unsplash.com/photo-1728544804234-9c36ab857d43?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwyfHxyYWluYm93JTIwY2hhcm0lMjBwZW5kYW50JTIwZ29sZCUyMGpld2Vscnl8ZW58MHx8fHwxNzkxODE3NzgzfDA&ixlib=rb-4.1.0&q=85" },
  { id: "piedino", label: "Piedino", icon: Footprints, image: "https://images.unsplash.com/photo-1606755612472-fafdff1005ca?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwzfHxiYWJ5JTIwZmVldCUyMGZvb3RwcmludCUyMHBlbmRhbnQlMjBnb2xkJTIwamV3ZWxyeXxlbnwwfHx8fDE3OTE4MTc3N3ww&ixlib=rb-4.1.0&q=85" },
  { id: "biberon", label: "Biberon", icon: Milk, image: "https://images.unsplash.com/photo-1749027862389-8229776cbb8b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwyfHxiYWJ5JTIwYm90dGxlJTIwY2hhcm0lMjBnb2xkJTIwamV3ZWxyeXxlbnwwfHx8fDE3OTE4MTc3Nzh8MA&ixlib=rb-4.1.0&q=85" },
];

const BASI_BRACCIALE = [
  { id: "rigido", label: "Bracciale Rigido", priceKey: "base_rigido" },
  { id: "morbido", label: "Bracciale Morbido", priceKey: "base_morbido" },
  { id: "collana", label: "Collana", priceKey: "base_collana" },
];

const TIPI_LETTERE = [
  { id: "lucide", label: "Lettere Lucide" },
  { id: "zirconate", label: "Lettere Zirconate" },
];

const SOGGETTI_LEGAMI = [
  { id: "bambino-zirconato", label: "Bambino zirconato", icon: Baby },
  { id: "bambina-zirconata", label: "Bambina zirconata", icon: Flower2 },
  { id: "cuore-zirconato", label: "Cuore zirconato", icon: Heart },
  { id: "zampa-zirconata", label: "Zampa zirconata", icon: PawPrint },
  { id: "quadrifoglio-zirconato", label: "Quadrifoglio zirconato", icon: Clover },
  { id: "stella-zirconata", label: "Stella zirconata", icon: Star },
  { id: "farfalla-zirconata", label: "Farfalla zirconata", icon: Sparkles },
  { id: "bambino-smaltato-blu", label: "Bambino smaltato blu", icon: Baby },
  { id: "bambina-smalto-rosa", label: "Bambina smalto rosa", icon: Flower2 },
  { id: "cuore-lucido", label: "Cuore lucido", icon: Heart },
];

const eur = (n) => `€${Number(n).toFixed(2).replace(".", ",")}`;

function OptionButton({ active, onClick, testid, children, large }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`relative w-full text-left ${large ? "px-6 py-5" : "px-5 py-4"} rounded-xl border transition-[border-color,background-color,box-shadow] duration-300 ${
        active
          ? "border-[#E2A3B1] bg-[#E2A3B1]/10 shadow-[0_0_0_1px_#E2A3B1]"
          : "border-[#F2DBD8] bg-white hover:border-[#E2A3B1]/60"
      }`}
    >
      {children}
      {active && (
        <span className="absolute top-3 right-3 w-5 h-5 rounded-full bg-[#E2A3B1] flex items-center justify-center">
          <Check className="w-3 h-3 text-[#37282B]" strokeWidth={3} />
        </span>
      )}
    </button>
  );
}

function StepTitle({ number, title, badge }) {
  return (
    <div className="flex items-center flex-wrap gap-4 mb-5">
      <span className="font-serif-display italic text-3xl text-[#E2A3B1]">{number}</span>
      <h3 className="font-serif-display text-2xl text-[#37282B]">{title}</h3>
      {badge && (
        <span className="text-[10px] uppercase tracking-[0.15em] font-semibold bg-[#8A9A86]/15 text-[#5F7263] px-3 py-1 rounded-full">
          {badge}
        </span>
      )}
    </div>
  );
}

const inputCls =
  "w-full px-5 py-3.5 rounded-xl border border-[#F2DBD8] bg-white text-[#37282B] text-sm placeholder:text-[#A89095] focus:outline-none focus:border-[#E2A3B1] focus:ring-2 focus:ring-[#E2A3B1]/25 transition-[border-color,box-shadow] duration-300";
const labelCls = "block text-xs uppercase tracking-[0.18em] font-semibold text-[#6B5459] mb-2";

function PendantsPreview({ galvanica, soggetti }) {
  const siteImgs = useSiteImages();
  const isRosa = galvanica.id === "bianco-lettere-rosa";
  const metal = isRosa ? "#D9D9DE" : galvanica.swatch;
  return (
    <div className="flex items-start justify-center gap-6 sm:gap-10 flex-wrap">
      {soggetti.map((s, i) => {
        const tipo = TIPI_SOGGETTO.find((t) => t.id === s.tipo);
        const Icon = tipo?.icon;
        return (
          <motion.div key={i} layout initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="flex flex-col items-center" data-testid={`config-preview-soggetto-${i + 1}`}>
            <div className="w-1 h-6 rounded-full" style={{ background: metal }} />
            <div className="w-20 h-20 rounded-full overflow-hidden shadow-md border-2 transition-colors duration-500 bg-white" style={{ borderColor: metal }}>
              {tipo?.image ? (
                <img src={pick(siteImgs, `soggetto_${tipo.id}`, tipo.image)} alt={tipo.label} className="w-full h-full object-contain" />
              ) : Icon ? (
                <span className="w-full h-full flex items-center justify-center" style={{ background: metal }}>
                  <Icon className="w-7 h-7 text-white/90" strokeWidth={1.5} />
                </span>
              ) : (
                <span className="w-full h-full flex items-center justify-center" style={{ background: metal }}>
                  <span className="text-white/85 font-serif-display italic text-2xl">?</span>
                </span>
              )}
            </div>
            <span className="mt-3 font-serif-display italic text-lg max-w-[7.5rem] leading-tight break-words" style={{ color: isRosa ? "#E2A3B1" : "#37282B" }} data-testid={`config-preview-incisione-${i + 1}`}>
              {s.incisione || "La tua incisione"}
            </span>
          </motion.div>
        );
      })}
    </div>
  );
}

function InizialiPreview({ galvanica, iniziali }) {
  const isRosa = galvanica.id === "bianco-lettere-rosa";
  const pendantBg = isRosa ? "#D9D9DE" : galvanica.swatch;
  const letterColor = isRosa ? "#E2A3B1" : "rgba(255,255,255,0.95)";
  return (
    <div className="flex items-start justify-center gap-6 sm:gap-10 flex-wrap">
      {iniziali.map((lettera, i) => (
        <motion.div key={i} layout initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col items-center" data-testid={`config-preview-iniziale-${i + 1}`}>
          <div className="w-1 h-6 rounded-full" style={{ background: pendantBg }} />
          <div className="w-20 h-20 rounded-full flex items-center justify-center shadow-md border-2 border-white/70 transition-colors duration-500" style={{ background: pendantBg }}>
            <span className="font-serif-display text-3xl" style={{ color: letterColor }}>{lettera || "?"}</span>
          </div>
          <span className="mt-3 text-xs uppercase tracking-[0.2em] text-[#A89095]">Iniziale {i + 1}</span>
        </motion.div>
      ))}
    </div>
  );
}

function LetterePreview({ galvanica, lettere, soggettiSel }) {
  const chars = lettere.split("").filter((c) => /[A-Z]/i.test(c));
  const letterBg = galvanica.id === "bianco-lettere-rosa" ? "#E2A3B1" : galvanica.swatch;
  return (
    <div data-testid="config-preview-lettere">
      <div className="flex items-center justify-center gap-2.5 flex-wrap">
        {chars.length === 0 && <span className="text-sm text-[#A89095] italic">Scrivi un nome per vedere l'anteprima</span>}
        {chars.map((c, i) => (
          <motion.span
            key={`${i}-${c}`}
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-11 h-11 rounded-lg flex items-center justify-center font-serif-display text-xl text-white/95 shadow-md border border-white/60"
            style={{ background: letterBg }}
            data-testid={`config-preview-lettera-${i}`}
          >
            {c.toUpperCase()}
          </motion.span>
        ))}
      </div>
      {soggettiSel.length > 0 && (
        <div className="mt-5 flex items-center justify-center gap-3 flex-wrap">
          {soggettiSel.map((s) => {
            const Icon = s.icon;
            return (
              <span key={s.id} className="inline-flex items-center gap-2 bg-[#E2A3B1]/10 border border-[#E2A3B1]/40 rounded-full px-4 py-2 text-xs font-medium text-[#37282B]" data-testid={`config-preview-soggetto-legami-${s.id}`}>
                <Icon className="w-4 h-4 text-[#C4758A]" strokeWidth={1.5} />
                {s.label}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Configurator() {
  const [collezione, setCollezione] = useState(null);
  const [catena, setCatena] = useState(CATENE[2]);
  const [galvanica, setGalvanica] = useState(GALVANICHE[0]);
  const [numSoggetti, setNumSoggetti] = useState(1);
  const [soggetti, setSoggetti] = useState([{ tipo: null, incisione: "" }]);
  const [baseBracciale, setBaseBracciale] = useState(BASI_BRACCIALE[0]);
  const [tipoLettere, setTipoLettere] = useState(TIPI_LETTERE[0]);
  const [perle, setPerle] = useState(PERLE[0]);
  const [soggettiLegami, setSoggettiLegami] = useState([]);
  const [lettere, setLettere] = useState("");
  const [numIniziali, setNumIniziali] = useState(1);
  const [iniziali, setIniziali] = useState([""]);
  const [form, setForm] = useState({
    cliente_nome: "", cliente_cognome: "", cliente_email: "",
    cliente_telefono: "", cliente_indirizzo: "", codice_fiscale: "", note: "",
  });
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(null);
  const [payReady, setPayReady] = useState(false);
  const [sconto, setSconto] = useState(null); // {code, type, value}
  const [codiceScontoInput, setCodiceScontoInput] = useState("");
  const [checkingSconto, setCheckingSconto] = useState(false);
  const [paypalCfg, setPaypalCfg] = useState({ enabled: false, client_id: "" });
  const [pricing, setPricing] = useState(DEFAULT_PRICING);
  const siteImgs = useSiteImages();

  useEffect(() => {
    axios.get(`${API}/paypal/config`).then(({ data }) => setPaypalCfg(data)).catch(() => {});
    axios.get(`${API}/pricing`).then(({ data }) => setPricing({ ...DEFAULT_PRICING, ...data })).catch(() => {});
    const handler = (e) => {
      const found = COLLEZIONI.find((c) => c.id === e.detail);
      if (found) { setCollezione(found); setGalvanica(GALVANICHE[0]); setCatena(CATENE[2]); }
    };
    window.addEventListener("mavi:select-collection", handler);
    return () => window.removeEventListener("mavi:select-collection", handler);
  }, []);

  const isLegami = collezione?.id === "legami";
  const isBonBon = collezione?.id === "bon-bon";
  const cateneOptions = collezione?.id === "petit" ? CATENE.filter((c) => c.id !== "perle") : CATENE;
  const hasCatena = collezione?.id === "petit" || collezione?.id === "my-letters";
  const hasSoggetti = collezione?.id === "petit" || collezione?.id === "moments" || isBonBon;
  const hasIncisione = hasSoggetti && soggetti.length > 0;
  const hasIniziali = collezione?.id === "my-letters";

  const changeNum = (n) => {
    setNumSoggetti(n);
    setSoggetti((prev) => {
      const next = [...prev];
      while (next.length < n) next.push({ tipo: null, incisione: "" });
      return next.slice(0, n);
    });
  };

  const changeNumIniziali = (n) => {
    setNumIniziali(n);
    setIniziali((prev) => {
      const next = [...prev];
      while (next.length < n) next.push("");
      return next.slice(0, n);
    });
  };

  const setSoggetto = (i, patch) => setSoggetti((prev) => prev.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const setIniziale = (i, v) => setIniziali((prev) => prev.map((x, idx) => (idx === i ? v.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 1) : x)));
  const toggleSoggettoLegami = (id) =>
    setSoggettiLegami((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const letterePulite = lettere.replace(/[^a-zA-ZàèéìòùÀÈÉÌÒÙ]/g, "").toUpperCase();

  const prezzoLettera = tipoLettere.id === "zirconate" ? pricing.lettera_zirconata : pricing.lettera_lucida;

  const subjectPrice = (id) => {
    if (collezione?.id === "bon-bon") return pricing.soggetto_bonbon;
    if (collezione?.id === "moments") return pricing[`moments_${id}`];
    return pricing[`petit_${id}`];
  };

  const prezzi = useMemo(() => {
    let parziale = 0;
    if (!collezione) return { parziale: 0, scontoImporto: 0, spedizione: pricing.spedizione, totale: 0 };
    if (collezione.id === "petit")
      parziale =
        pricing[`catena_${catena.id}`] +
        soggetti.reduce((t, s) => t + (s.tipo ? pricing[`petit_${s.tipo}`] || 0 : 0), 0);
    else if (collezione.id === "moments")
      parziale = soggetti.reduce((t, s) => t + (s.tipo ? pricing[`moments_${s.tipo}`] || 0 : 0), 0);
    else if (collezione.id === "legami")
      parziale =
        pricing[baseBracciale.priceKey] +
        prezzoLettera * Math.max(letterePulite.length, 1) +
        soggettiLegami.reduce((t, id) => t + (pricing[`legami_${id}`] || 0), 0);
    else if (collezione.id === "my-letters") parziale = pricing[`catena_${catena.id}`] + pricing.iniziale * numIniziali;
    else if (collezione.id === "bon-bon") parziale = pricing.base_bonbon + pricing.soggetto_bonbon * numSoggetti;
    const scontoImporto = sconto ? (sconto.type === "percent" ? Math.round(parziale * sconto.value) / 100 : Math.min(sconto.value, parziale)) : 0;
    const netto = Math.max(parziale - scontoImporto, 0);
    const spedizione = netto >= pricing.spedizione_gratis_sopra ? 0 : pricing.spedizione;
    return { parziale, scontoImporto, spedizione, totale: netto + spedizione };
  }, [collezione, catena, numSoggetti, numIniziali, letterePulite, baseBracciale, soggettiLegami, soggetti, pricing, tipoLettere, sconto]);

  const applicaSconto = async () => {
    const code = codiceScontoInput.trim();
    if (!code) return;
    setCheckingSconto(true);
    try {
      const { data } = await axios.post(`${API}/discounts/validate`, { code, subtotal: prezzi.parziale, email: form.email || undefined });
      if (data.valid) {
        setSconto({ code: data.code, type: data.type, value: data.value });
        setPayReady(false);
        toast.success(`Codice ${data.code} applicato!`);
      } else {
        setSconto(null);
        toast.error(data.message);
      }
    } catch {
      toast.error("Errore nella verifica del codice sconto.");
    } finally {
      setCheckingSconto(false);
    }
  };

  const rimuoviSconto = () => { setSconto(null); setCodiceScontoInput(""); setPayReady(false); };

  const buildPayload = () => ({
    collezione: collezione.nome,
    collezione_image: collezione.image,
    catena: hasCatena ? catena.label : isBonBon ? CATENE[1].label : "",
    base: isLegami ? baseBracciale.label : "",
    perle: isBonBon ? perle.label : "",
    galvanica: galvanica.label,
    soggetti: hasSoggetti
      ? soggetti.map((s) => {
          const t = TIPI_SOGGETTO.find((x) => x.id === s.tipo);
          return { tipo: t?.label || s.tipo, incisione: s.incisione, image: pick(siteImgs, `soggetto_${s.tipo}`, t?.image || "") };
        })
      : isLegami
        ? soggettiLegami.map((id) => ({ tipo: SOGGETTI_LEGAMI.find((s) => s.id === id)?.label || id, incisione: "", image: pick(siteImgs, `soggetto_legami_${id}`, "") }))
        : [],
    lettere: isLegami ? letterePulite : "",
    lettere_tipo: isLegami ? tipoLettere.label : "",
    iniziali: hasIniziali ? iniziali.filter(Boolean) : [],
    prezzo_totale: prezzi.totale,
    spedizione_gratuita: prezzi.spedizione === 0,
    codice_sconto: sconto?.code || "",
    sconto_importo: prezzi.scontoImporto || 0,
    ...form,
  });

  const validate = () => {
    if (!collezione) { toast.error("Scegli prima una collezione."); return false; }
    if (hasSoggetti && soggetti.some((s) => !s.tipo)) { toast.error("Scegli il tipo di ogni soggetto."); return false; }
    if (isLegami && letterePulite.length === 0) { toast.error("Scrivi le lettere da comporre sul bracciale."); return false; }
    if (hasIniziali && iniziali.some((x) => !x)) { toast.error("Scegli tutte le iniziali."); return false; }
    if (!form.cliente_nome.trim() || !form.cliente_cognome.trim() || !form.cliente_email.trim() ||
        !form.cliente_telefono.trim() || !form.cliente_indirizzo.trim() || !form.codice_fiscale.trim()) {
      toast.error("Compila tutti i dati obbligatori: nome, cognome, email, telefono, indirizzo e codice fiscale.");
      return false;
    }
    return true;
  };

  const handleSubmitNoPayment = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/order-requests?via=paypal`, buildPayload());
      if (PAYPAL_EMAIL) {
        toast.success(`Ordine #${data.order_id} registrato! Reindirizzamento a PayPal…`);
        const paypalUrl =
          `https://www.paypal.com/cgi-bin/webscr?cmd=_xclick` +
          `&business=${encodeURIComponent(PAYPAL_EMAIL)}` +
          `&item_name=${encodeURIComponent(`Ma.Vi Gioielli — Ordine #${data.order_id}`)}` +
          `&amount=${prezzi.totale.toFixed(2)}` +
          `&currency_code=EUR&no_shipping=1` +
          `&custom=${data.order_id}` +
          `&notify_url=${encodeURIComponent(`${API}/paypal/ipn`)}` +
          `&return=${encodeURIComponent(window.location.origin)}`;
        window.setTimeout(() => {
          window.location.href = paypalUrl;
        }, 1200);
        return;
      }
      setSuccess(data.order_id);
      toast.success("Ordine inviato con successo!");
    } catch (err) {
      toast.error("Qualcosa è andato storto. Riprova tra poco.");
    } finally {
      setSending(false);
    }
  };

  const sections = [];
  sections.push({
    key: "collezione", title: "Scegli la collezione",
    content: (
      <div className="flex flex-col gap-3">
        {COLLEZIONI.map((c) => {
          const active = collezione?.id === c.id;
          return (
            <button
              type="button"
              key={c.id}
              onClick={() => { setCollezione(c); setPayReady(false); setGalvanica(GALVANICHE[0]); setCatena(CATENE[2]); setNumSoggetti(1); setSoggetti([{ tipo: null, incisione: "" }]); }}
              data-testid={`config-collezione-${c.id}`}
              className={`relative overflow-hidden rounded-2xl border text-left flex items-stretch transition-[border-color,box-shadow] duration-300 ${
                active ? "border-[#E2A3B1] shadow-[0_0_0_2px_#E2A3B1]" : "border-[#F2DBD8] hover:border-[#E2A3B1]/60"
              }`}
            >
              <div className="w-28 sm:w-36 shrink-0 bg-[#FBECEA]">
                <img src={pick(siteImgs, `collezione_${c.id}`, c.image)} alt={c.nome} className="w-full h-full object-contain" data-testid={`config-collezione-image-${c.id}`} />
              </div>
              <div className="p-4 sm:p-5 bg-white flex-1 flex flex-col justify-center">
                <span className="font-serif-display text-lg sm:text-xl text-[#37282B]">{c.nome}</span>
                <span className="block text-xs sm:text-sm text-[#A89095] mt-1">{c.desc}</span>
              </div>
              {active && (
                <span className="absolute top-3 right-3 w-6 h-6 rounded-full bg-[#E2A3B1] flex items-center justify-center shadow">
                  <Check className="w-3.5 h-3.5 text-[#37282B]" strokeWidth={3} />
                </span>
              )}
            </button>
          );
        })}
      </div>
    ),
  });

  if (hasCatena) {
    sections.push({
      key: "catena", title: "Scegli il modello di catena",
      content: (
        <div className="grid sm:grid-cols-3 gap-3">
          {cateneOptions.map((c) => {
            const active = catena.id === c.id;
            return (
              <button
                type="button"
                key={c.id}
                onClick={() => setCatena(c)}
                data-testid={`config-catena-${c.id}`}
                className={`relative overflow-hidden rounded-2xl border text-left transition-[border-color,box-shadow] duration-300 ${
                  active ? "border-[#E2A3B1] shadow-[0_0_0_2px_#E2A3B1]" : "border-[#F2DBD8] hover:border-[#E2A3B1]/60"
                }`}
              >
                <div className="aspect-[4/3] w-full overflow-hidden bg-[#FBECEA]">
                  <img src={pick(siteImgs, `catena_${c.id}`, c.image)} alt={c.label} className="w-full h-full object-contain" data-testid={`config-catena-image-${c.id}`} />
                </div>
                <div className="p-4 bg-white">
                  <span className="text-sm font-semibold text-[#37282B]">{c.label}</span>
                  <span className="block text-xs text-[#A89095] mt-1">{eur(pricing[`catena_${c.id}`])}</span>
                </div>
                {active && (
                  <span className="absolute top-3 right-3 w-6 h-6 rounded-full bg-[#E2A3B1] flex items-center justify-center shadow">
                    <Check className="w-3.5 h-3.5 text-[#37282B]" strokeWidth={3} />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      ),
    });
  }

  if (isLegami) {
    sections.push({
      key: "base", title: "Scegli la base",
      content: (
        <div className="grid sm:grid-cols-3 gap-4 items-start">
          {BASI_BRACCIALE.map((b) => {
            const foto = pick(siteImgs, `base_${b.id}`, null);
            return (
              <OptionButton key={b.id} large active={baseBracciale.id === b.id} onClick={() => setBaseBracciale(b)} testid={`config-base-${b.id}`}>
                {foto && (
                  <span className="block aspect-square w-full overflow-hidden rounded-lg bg-[#FBECEA] mb-3">
                    <img src={foto} alt={b.label} loading="lazy" className="w-full h-full object-contain" data-testid={`config-base-image-${b.id}`} />
                  </span>
                )}
                <span className="text-base font-semibold text-[#37282B]">{b.label}</span>
                <span className="block text-sm text-[#A89095] mt-1">{eur(pricing[b.priceKey] || 0)}</span>
              </OptionButton>
            );
          })}
        </div>
      ),
    });
  }

  if (collezione) {
    sections.push({
      key: "galvanica", title: "Colore della galvanica",
      content: (
        <div className="grid sm:grid-cols-3 gap-3">
          {(isLegami || collezione?.id === "my-letters" || collezione?.id === "petit" ? GALVANICHE_LETTERE_ROSA : GALVANICHE).map((g) => (
            <OptionButton key={g.id} active={galvanica.id === g.id} onClick={() => setGalvanica(g)} testid={`config-galvanica-${g.id}`}>
              <span className="flex items-center gap-3">
                <span className="w-7 h-7 rounded-full border border-[#37282B]/10 shrink-0" style={{ background: g.swatch }} />
                <span className="text-sm font-semibold text-[#37282B]">{g.label}</span>
              </span>
            </OptionButton>
          ))}
        </div>
      ),
    });
  }

  if (isBonBon) {
    sections.push({
      key: "perle", title: "Colore delle perle",
      content: (
        <>
          <p className="text-sm text-[#6B5459] mb-4 italic">La collana Bon Bon si realizza solo con collana di perle.</p>
          <div className="grid sm:grid-cols-3 gap-3">
          {PERLE.map((p) => {
            const foto = pick(siteImgs, `perle_${p.id}`, null);
            return (
              <OptionButton key={p.id} active={perle.id === p.id} onClick={() => setPerle(p)} testid={`config-perle-${p.id}`}>
                {foto ? (
                  <span className="block w-full">
                    <span className="block aspect-[4/3] w-full overflow-hidden rounded-lg bg-[#FBECEA] mb-2">
                      <img src={foto} alt={p.label} loading="lazy" className="w-full h-full object-contain" data-testid={`config-perle-image-${p.id}`} />
                    </span>
                    <span className="text-sm font-semibold text-[#37282B]">{p.label}</span>
                  </span>
                ) : (
                  <span className="flex items-center gap-3">
                    <span className="w-7 h-7 rounded-full border border-[#37282B]/10 shrink-0" style={{ background: p.swatch }} />
                    <span className="text-sm font-semibold text-[#37282B]">{p.label}</span>
                  </span>
                )}
              </OptionButton>
            );
          })}
          </div>
        </>
      ),
    });
  }

  if (isLegami) {
    sections.push({
      key: "tipo-lettere", title: "Tipo di lettere",
      content: (
        <div className="grid sm:grid-cols-2 gap-3">
          {TIPI_LETTERE.map((tl) => (
            <OptionButton key={tl.id} active={tipoLettere.id === tl.id} onClick={() => setTipoLettere(tl)} testid={`config-tipo-lettere-${tl.id}`}>
              <span className="text-sm font-semibold text-[#37282B]">{tl.label}</span>
              <span className="block text-xs text-[#A89095] mt-1">
                {eur(tl.id === "zirconate" ? pricing.lettera_zirconata : pricing.lettera_lucida)} a lettera
              </span>
            </OptionButton>
          ))}
        </div>
      ),
    });
    sections.push({
      key: "lettere", title: "Cosa vuoi scrivere?",
      content: (
        <>
          <label className={labelCls} htmlFor="lettere-bracciale">Nome o parola da comporre *</label>
          <input id="lettere-bracciale" value={lettere} onChange={(e) => setLettere(e.target.value)} placeholder="Es. SOFIA, MAMMA, AMORE…" maxLength={14} className={`${inputCls} uppercase tracking-[0.3em]`} data-testid="config-input-lettere" />
          <p className="mt-2 text-xs text-[#A89095]">{letterePulite.length} lettere · {eur(prezzoLettera)} a lettera</p>
        </>
      ),
    });
    sections.push({
      key: "soggetti-legami", title: "Aggiungi un soggetto", badge: "Facoltativo",
      content: (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5">
          {SOGGETTI_LEGAMI.map((s) => {
            const Icon = s.icon;
            const active = soggettiLegami.includes(s.id);
            const foto = pick(siteImgs, `soggetto_legami_${s.id}`, null);
            return (
              <button
                type="button"
                key={s.id}
                onClick={() => toggleSoggettoLegami(s.id)}
                data-testid={`config-soggetto-legami-${s.id}`}
                className={`flex flex-col items-center gap-1.5 px-2 py-3 rounded-xl border transition-[border-color,background-color] duration-300 ${
                  active ? "border-[#E2A3B1] bg-[#E2A3B1]/10" : "border-[#F2DBD8] bg-white hover:border-[#E2A3B1]/60"
                }`}
              >
                {foto ? (
                  <span className="w-full aspect-square rounded-lg overflow-hidden bg-[#FBECEA]">
                    <img src={foto} alt={s.label} loading="lazy" className="w-full h-full object-contain" data-testid={`config-soggetto-legami-image-${s.id}`} />
                  </span>
                ) : (
                  <Icon className={`w-5 h-5 ${active ? "text-[#C4758A]" : "text-[#A89095]"}`} strokeWidth={1.5} />
                )}
                <span className="text-[11px] font-medium text-[#37282B] text-center leading-tight">{s.label}</span>
                <span className="text-[10px] text-[#A89095]">+ {eur(pricing[`legami_${s.id}`] || 0)}</span>
              </button>
            );
          })}
        </div>
      ),
    });
  }

  if (hasSoggetti) {
    sections.push({
      key: "soggetti", title: "Scegli i soggetti", badge: numSoggetti === 0 ? "Nessun soggetto" : `${numSoggetti} selezionat${numSoggetti > 1 ? "i" : "o"}`,
      content: (
        <>
          <div className={`grid gap-3 mb-6 ${isBonBon ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-3"}`}>
            {(isBonBon ? [0, 1, 2, 3] : [1, 2, 3]).map((n) => (
              <OptionButton key={n} active={numSoggetti === n} onClick={() => changeNum(n)} testid={`config-num-soggetti-${n}`}>
                <span className="text-sm font-semibold text-[#37282B]">{n === 0 ? "Nessun soggetto" : `${n} soggett${n > 1 ? "i" : "o"}`}</span>
                <span className="block text-xs text-[#A89095] mt-1">
                  {n === 0 ? "Solo collana perle" : collezione.id === "bon-bon" ? `+ ${eur(pricing.soggetto_bonbon * n)}` : "prezzo su ogni soggetto"}
                </span>
              </OptionButton>
            ))}
          </div>
          <div className="space-y-6">
            {soggetti.map((s, i) => (
              <div key={i} className="rounded-2xl border border-[#F2DBD8] bg-white p-5" data-testid={`config-soggetto-${i + 1}`}>
                <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#C4758A]">Soggetto {i + 1}</span>
                <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2.5 mt-3">
                  {TIPI_SOGGETTO.map((t) => {
                    const active = s.tipo === t.id;
                    return (
                      <button
                        type="button"
                        key={t.id}
                        onClick={() => setSoggetto(i, { tipo: t.id })}
                        data-testid={`config-soggetto-${i + 1}-tipo-${t.id}`}
                        className={`relative overflow-hidden rounded-xl border transition-[border-color,box-shadow] duration-300 ${
                          active ? "border-[#E2A3B1] shadow-[0_0_0_2px_#E2A3B1]" : "border-[#F2DBD8] hover:border-[#E2A3B1]/60"
                        }`}
                      >
                        <div className="aspect-square w-full overflow-hidden bg-[#FBECEA]">
                          <img src={pick(siteImgs, `soggetto_${t.id}`, t.image)} alt={t.label} loading="lazy" className="w-full h-full object-contain" />
                        </div>
                        <span className="block pt-1.5 text-[11px] font-medium text-[#37282B] text-center bg-white leading-tight">{t.label}</span>
                        <span className="block pb-1.5 text-[10px] text-[#A89095] text-center bg-white">{eur(subjectPrice(t.id))}</span>
                        {active && (
                          <span className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-[#E2A3B1] flex items-center justify-center shadow">
                            <Check className="w-3 h-3 text-[#37282B]" strokeWidth={3} />
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </>
      ),
    });
  }

  if (hasIniziali) {
    sections.push({
      key: "iniziali", title: "Scegli le iniziali",
      content: (
        <>
          <div className="grid grid-cols-3 gap-3 mb-5">
            {[1, 2, 3].map((n) => (
              <OptionButton key={n} active={numIniziali === n} onClick={() => changeNumIniziali(n)} testid={`config-num-iniziali-${n}`}>
                <span className="text-sm font-semibold text-[#37282B]">{n} inizial{n > 1 ? "i" : "e"}</span>
                <span className="block text-xs text-[#A89095] mt-1">+ {eur(pricing.iniziale * n)}</span>
              </OptionButton>
            ))}
          </div>
          <div className="flex gap-3">
            {iniziali.map((v, i) => (
              <input key={i} value={v} onChange={(e) => setIniziale(i, e.target.value)} maxLength={1} placeholder="A"
                className="w-16 h-16 text-center rounded-xl border border-[#F2DBD8] bg-white text-[#37282B] font-serif-display text-3xl uppercase focus:outline-none focus:border-[#E2A3B1] focus:ring-2 focus:ring-[#E2A3B1]/25 transition-[border-color,box-shadow] duration-300"
                data-testid={`config-input-iniziale-${i + 1}`} />
            ))}
          </div>
        </>
      ),
    });
  }

  if (hasIncisione) {
    sections.push({
      key: "incisione", title: "Decidi l'incisione", badge: "Gratuita",
      content: (
        <div className="space-y-4">
          {soggetti.map((s, i) => (
            <div key={i}>
              <label className={labelCls} htmlFor={`incisione-${i}`}>
                <PenLine className="inline w-3.5 h-3.5 mr-1.5 -mt-0.5" />
                Incisione soggetto {i + 1}{s.tipo ? ` — ${TIPI_SOGGETTO.find((t) => t.id === s.tipo)?.label}` : ""}
              </label>
              <input id={`incisione-${i}`} value={s.incisione} onChange={(e) => setSoggetto(i, { incisione: e.target.value })} placeholder="Es. Aurora · 12.03.2024" className={inputCls} data-testid={`config-input-incisione-${i + 1}`} />
            </div>
          ))}
          <p className="text-xs text-[#A89095] italic">
            L'incisione è sempre inclusa nel prezzo, senza costi aggiuntivi.
            <br />
            Dove possibile, l'incisione verrà fatta sul davanti del soggetto.
          </p>
        </div>
      ),
    });
  }

  const previewSection = () => {
    if (hasSoggetti) return <PendantsPreview galvanica={galvanica} soggetti={soggetti} />;
    if (hasIniziali) return <InizialiPreview galvanica={galvanica} iniziali={iniziali} />;
    if (isLegami)
      return (
        <LetterePreview
          galvanica={galvanica}
          lettere={letterePulite}
          soggettiSel={soggettiLegami.map((id) => SOGGETTI_LEGAMI.find((s) => s.id === id)).filter(Boolean)}
        />
      );
    return null;
  };

  return (
    <section id="configura" className="py-28 lg:py-36 bg-[#FBECEA] relative grain-overlay" data-testid="configurator-section">
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.8 }} className="text-center mb-16">
          <span className="text-xs uppercase tracking-[0.3em] font-semibold text-[#C4758A]" data-testid="configurator-overline">Il Configuratore</span>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-4 text-[#37282B]" data-testid="configurator-headline">
            Componi il tuo gioiello <span className="italic text-[#E2A3B1]">unico</span>
          </h2>
          <p className="mt-5 text-base sm:text-lg text-[#6B5459] max-w-xl mx-auto" data-testid="configurator-subtitle">
            Scegli la collezione e personalizzala passo dopo passo.
          </p>
          <p className="mt-3 inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] font-semibold text-[#5F7263] bg-[#8A9A86]/15 px-4 py-2 rounded-full" data-testid="configurator-shipping-note">
            <Truck className="w-4 h-4" /> Spedizione gratuita sopra {eur(pricing.spedizione_gratis_sopra)}
          </p>
        </motion.div>

        <AnimatePresence mode="wait">
          {success ? (
            <motion.div key="success" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }}
              className="bg-white rounded-3xl border border-[#E2A3B1]/40 p-12 text-center shadow-xl shadow-[#37282B]/5" data-testid="order-success-panel">
              <span className="inline-flex w-16 h-16 rounded-full bg-[#E2A3B1]/15 items-center justify-center mb-6">
                <Check className="w-8 h-8 text-[#E2A3B1]" />
              </span>
              <h3 className="font-serif-display text-3xl text-[#37282B]" data-testid="order-success-title">Ordine inviato!</h3>
              <p className="mt-4 text-[#6B5459] max-w-md mx-auto" data-testid="order-success-message">
                Grazie per aver scelto Ma.Vi Gioielli. Il tuo riferimento è <strong className="text-[#C4758A]">#{success}</strong>.
                Ti contatteremo presto via email o WhatsApp per confermare tutti i dettagli.
              </p>
              <button onClick={() => { setSuccess(null); setPayReady(false); }} data-testid="order-new-button"
                className="mt-8 px-8 py-3.5 bg-[#37282B] text-[#FDF8F7] text-xs uppercase tracking-[0.2em] font-semibold rounded-full hover:bg-[#E2A3B1] hover:text-[#37282B] transition-colors duration-300">
                Crea un altro gioiello
              </button>
            </motion.div>
          ) : (
            <motion.form key="form" onSubmit={handleSubmitNoPayment} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.8 }}
              className="bg-[#FDF8F7] rounded-3xl border border-[#F2DBD8] p-8 sm:p-12 shadow-xl shadow-[#37282B]/5 space-y-14" data-testid="configurator-form">

              {sections.map((s, i) => (
                <div key={s.key}>
                  <StepTitle number={String(i + 1).padStart(2, "0")} title={s.title} badge={s.badge} />
                  {s.content}
                </div>
              ))}

              {collezione && (
                <div className="rounded-2xl border border-[#F2DBD8] bg-white p-6 sm:p-8 text-center" data-testid="config-preview">
                  <span className="text-xs uppercase tracking-[0.25em] font-semibold text-[#C4758A]">Anteprima della tua creazione</span>
                  <div className="mt-6">{previewSection()}</div>
                  <p className="mt-5 text-xs text-[#A89095]">
                    {`Galvanica ${galvanica.label} · `}anteprima indicativa, la creazione finale è fatta a mano
                  </p>
                </div>
              )}

              {collezione && (
                <div>
                  <StepTitle number={String(sections.length + 1).padStart(2, "0")} title="I tuoi dati per l'ordine" />
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className={labelCls} htmlFor="cliente-nome">Nome *</label>
                      <input id="cliente-nome" value={form.cliente_nome} onChange={set("cliente_nome")} placeholder="Maria" className={inputCls} data-testid="config-input-nome" required />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="cliente-cognome">Cognome *</label>
                      <input id="cliente-cognome" value={form.cliente_cognome} onChange={set("cliente_cognome")} placeholder="Rossi" className={inputCls} data-testid="config-input-cognome" required />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="cliente-email">Email *</label>
                      <input id="cliente-email" type="email" value={form.cliente_email} onChange={set("cliente_email")} placeholder="nome@email.it" className={inputCls} data-testid="config-input-email" required />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="cliente-telefono">Numero di telefono *</label>
                      <input id="cliente-telefono" type="tel" value={form.cliente_telefono} onChange={set("cliente_telefono")} placeholder="333 1234567" className={inputCls} data-testid="config-input-telefono" required />
                    </div>
                    <div className="sm:col-span-2">
                      <label className={labelCls} htmlFor="cliente-indirizzo">Indirizzo di spedizione *</label>
                      <input id="cliente-indirizzo" value={form.cliente_indirizzo} onChange={set("cliente_indirizzo")} placeholder="Via, numero civico, CAP, Città (Provincia)" className={inputCls} data-testid="config-input-indirizzo" required />
                    </div>
                    <div className="sm:col-span-2">
                      <label className={labelCls} htmlFor="codice-fiscale">Codice fiscale *</label>
                      <input id="codice-fiscale" value={form.codice_fiscale} onChange={(e) => setForm((f) => ({ ...f, codice_fiscale: e.target.value.toUpperCase() }))} placeholder="RSSMRA..." maxLength={16} className={`${inputCls} uppercase tracking-widest`} data-testid="config-input-codice-fiscale" required />
                    </div>
                    <div className="sm:col-span-2">
                      <label className={labelCls} htmlFor="note">Note per l'artigiano</label>
                      <input id="note" value={form.note} onChange={set("note")} placeholder="Richieste particolari…" className={inputCls} data-testid="config-input-note" />
                    </div>
                  </div>
                </div>
              )}

              {collezione && (
                <div className="pt-6 border-t border-[#F2DBD8] space-y-6">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
                    <div data-testid="config-price-estimate">
                      <span className="text-xs uppercase tracking-[0.2em] text-[#A89095]">Riepilogo</span>
                      <p className="text-sm text-[#6B5459] mt-1">
                        {collezione.nome}
                        {" · "}
                        {prezzi.spedizione === 0 ? (
                          <span className="text-[#5F7263] font-semibold">Spedizione gratuita</span>
                        ) : (
                          <span>Spedizione {eur(prezzi.spedizione)}</span>
                        )}
                      </p>
                      {prezzi.spedizione > 0 && (
                        <p className="text-xs text-[#C4758A] mt-1" data-testid="config-freeship-hint">
                          Spedizione gratuita sopra {eur(pricing.spedizione_gratis_sopra)}
                        </p>
                      )}
                      <p className="font-serif-display text-4xl text-[#37282B] mt-1" data-testid="config-total-price">{eur(prezzi.totale)}</p>
                      {prezzi.scontoImporto > 0 && (
                        <p className="text-sm text-[#5F7263] font-semibold mt-1" data-testid="config-discount-line">
                          Sconto {sconto.code}: −{eur(prezzi.scontoImporto)}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="max-w-md mx-auto w-full" data-testid="config-discount-section">
                    {!sconto ? (
                      <div className="flex gap-2">
                        <input
                          value={codiceScontoInput}
                          onChange={(e) => setCodiceScontoInput(e.target.value.toUpperCase())}
                          placeholder="Hai un codice sconto?"
                          className={`${inputCls} uppercase tracking-widest flex-1`}
                          data-testid="config-input-discount"
                        />
                        <button
                          type="button"
                          onClick={applicaSconto}
                          disabled={checkingSconto || !codiceScontoInput.trim()}
                          data-testid="config-discount-apply"
                          className="px-5 py-3 border border-[#37282B] text-[#37282B] text-xs uppercase tracking-[0.15em] font-semibold rounded-full hover:bg-[#37282B] hover:text-[#FDF8F7] transition-colors duration-300 disabled:opacity-50 shrink-0"
                        >
                          {checkingSconto ? "…" : "Applica"}
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between rounded-xl border border-[#5F7263]/30 bg-[#5F7263]/10 px-4 py-3" data-testid="config-discount-applied">
                        <span className="text-sm text-[#5F7263] font-semibold">Codice {sconto.code} applicato</span>
                        <button type="button" onClick={rimuoviSconto} data-testid="config-discount-remove"
                          className="text-xs uppercase tracking-[0.12em] text-[#A89095] hover:text-[#C4758A] transition-colors font-semibold">
                          Rimuovi
                        </button>
                      </div>
                    )}
                  </div>

                  {paypalCfg.enabled ? (
                    payReady ? (
                      <div className="max-w-md mx-auto" data-testid="config-paypal-buttons">
                        <PayPalScriptProvider options={{ clientId: paypalCfg.client_id, currency: "EUR", locale: "it_IT" }}>
                          <PayPalButtons
                            style={{ layout: "vertical", shape: "pill", color: "gold", label: "pay" }}
                            forceReRender={[prezzi.totale]}
                            createOrder={async () => {
                              const { data } = await axios.post(`${API}/paypal/create-order`, {
                                amount: prezzi.totale,
                                description: `Ma.Vi Gioielli — ${collezione.nome}`,
                                discount_code: sconto?.code || null,
                                subtotal: prezzi.parziale,
                                customer_email: form.email || null,
                              });
                              return data.id;
                            }}
                            onApprove={async (d) => {
                              try {
                                const { data } = await axios.post(`${API}/paypal/capture-order`, {
                                  paypal_order_id: d.orderID,
                                  order: buildPayload(),
                                });
                                setSuccess(data.order_id);
                                toast.success("Pagamento completato! Ordine inviato.");
                              } catch (err) {
                                toast.error("Pagamento non confermato. Contattaci su WhatsApp.");
                              }
                            }}
                            onError={() => toast.error("Errore PayPal. Riprova o contattaci su WhatsApp.")}
                          />
                        </PayPalScriptProvider>
                      </div>
                    ) : (
                      <button type="button" onClick={() => { if (validate()) setPayReady(true); }} data-testid="config-submit-button"
                        className="w-full sm:w-auto mx-auto flex justify-center px-10 py-4 bg-[#37282B] text-[#FDF8F7] text-xs uppercase tracking-[0.2em] font-semibold rounded-full items-center gap-3 hover:bg-[#E2A3B1] hover:text-[#37282B] transition-colors duration-300">
                        Procedi al pagamento — {eur(prezzi.totale)}
                      </button>
                    )
                  ) : (
                    <div className="text-center space-y-3">
                      <button type="submit" disabled={sending} data-testid="config-submit-button"
                        className="group px-10 py-4 bg-[#37282B] text-[#FDF8F7] text-xs uppercase tracking-[0.2em] font-semibold rounded-full inline-flex items-center gap-3 hover:bg-[#E2A3B1] hover:text-[#37282B] transition-colors duration-300 disabled:opacity-60">
                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />}
                        {sending ? "Attendere…" : `Procedi al pagamento — ${eur(prezzi.totale)}`}
                      </button>
                      <p className="text-xs text-[#A89095]" data-testid="config-payment-note">
                        L'ordine viene registrato e poi verrai reindirizzata su PayPal per pagare in sicurezza.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </motion.form>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
